import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import {normalizeHex} from '@amblique/king-living/app/utils/normalize-hex';

const helpers = createMultiStyleConfigHelpers(['root', 'icon', 'wrapper', 'title']);

/** Default and bounds for Contentful `maxItemsPerRowDesktop` (e.g. 3 freeform, 4 range PLP). */
const ICON_SET_DESKTOP_COLS_DEFAULT = 3;
const ICON_SET_DESKTOP_COLS_MIN = 2;
const ICON_SET_DESKTOP_COLS_MAX = 6;
const ICON_SET_DESKTOP_GAP_PX = 40;
const PRESS_LOGOS_MOBILE_COLUMN_GAP_PX = 10;
const PRESS_LOGOS_DESKTOP_COLUMN_GAP_PX = 40;
const PRESS_LOGOS_MOBILE_COL_BASIS =
  `calc((100% - 2 * ${PRESS_LOGOS_MOBILE_COLUMN_GAP_PX}px) / 3)`;

/**
 * @param {number|null|undefined} value - Contentful IconSet.maxItemsPerRowDesktop
 * @returns {number}
 */
export function clampIconSetDesktopColumns(value) {
  if (value == null) {
    return ICON_SET_DESKTOP_COLS_DEFAULT;
  }

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return ICON_SET_DESKTOP_COLS_DEFAULT;
  }

  return Math.min(
    ICON_SET_DESKTOP_COLS_MAX,
    Math.max(ICON_SET_DESKTOP_COLS_MIN, Math.round(n))
  );
}

const WRAPPER_PADDING = {
  paddingTop: {base: 20, md: 20},
  paddingBottom: {base: 20, md: 20},
  paddingLeft: {base: 5, md: 5},
  paddingRight: {base: 5, md: 5},
};

const SHARED_ROOT_BASE = {
  width: '100%',
  maxWidth: 'iconSetMax',
  margin: '0 auto',
  backgroundColor: 'transparent',
  display: 'flex',
  flexWrap: 'wrap',
  gap: {base: 12, md: `${ICON_SET_DESKTOP_GAP_PX}px`},
  alignItems: 'stretch',
};

/**
 * @param {{ component?: { maxItemsPerRowDesktop?: number|null }, lastRowIsOdd?: boolean }} props - style props from useMultiStyleConfig
 * @returns {object}
 */
function getGridRoot(props) {
  const lastRowIsOdd = Boolean(props?.lastRowIsOdd);

  return {
    ...SHARED_ROOT_BASE,
    justifyContent: {
      base: 'center',
      md: lastRowIsOdd ? 'center' : 'flex-start',
    },
  };
}

/**
 * @param {{ component?: { maxItemsPerRowDesktop?: number|null } }} props - style props from useMultiStyleConfig
 * @returns {object}
 */
function getIconItemStyles(props) {
  const desktopCols = clampIconSetDesktopColumns(props?.component?.maxItemsPerRowDesktop);
  const desktopBasis = `calc((100% - (${desktopCols} - 1) * ${ICON_SET_DESKTOP_GAP_PX}px) / ${desktopCols})`;
  const mobileBasis = 'calc((100% - var(--chakra-space-12, 3rem)) / 2)';

  return {
    gap: 4,
    width: {base: mobileBasis, md: desktopBasis},
    flexBasis: {base: mobileBasis, md: desktopBasis},
    minWidth: {base: mobileBasis, md: desktopBasis},
    maxWidth: {base: mobileBasis, md: desktopBasis},
  };
}

function getWrapperStyles({component}) {
  const bgHex = normalizeHex(component?.backgroundColour);
  const textHex = normalizeHex(component?.textColour);

  return {
    width: '100%',
    backgroundColor: bgHex ?? 'white',
    ...(textHex && {color: textHex}),
    ...WRAPPER_PADDING,
  };
}

/**
 * @param {{ component?: { maxItemsPerRowDesktop?: number|null }, lastRowIsOdd?: boolean }} props
 * @returns {{wrapper: object, root: object, icon: object}}
 */
function getVariantStyles(props) {
  return {
    wrapper: getWrapperStyles(props),
    root: getGridRoot(props),
    icon: getIconItemStyles(props),
  };
}

/**
 * CF-053 press logos — tokens from former homepage-only CSS.
 * Descendant rules travel under `sx` so Chakra does not drop them when spread as Box props.
 */
function getPressLogosStyles({component}) {
  const bgHex = normalizeHex(component?.backgroundColour);
  const textHex = normalizeHex(component?.textColour);

  return {
    wrapper: {
      width: '100%',
      backgroundColor: bgHex ?? 'white',
      ...(textHex && {color: textHex}),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: {base: '64px', lg: '24px'},
      paddingTop: {base: '56px', lg: '80px'},
      paddingBottom: {base: '56px', lg: '120px'},
      paddingLeft: '20px',
      paddingRight: '20px',
    },
    title: {
      display: 'block',
      width: '100%',
      maxWidth: '1280px',
      margin: 0,
      textAlign: 'left',
      color: textHex ?? 'brandColors.primaryDarkLinen',
      fontFamily: 'body',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '120%',
      letterSpacing: '2.4px',
      textTransform: 'uppercase',
    },
    root: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: {base: 'wrap', lg: 'nowrap'},
      alignItems: 'center',
      alignContent: {base: 'center', lg: 'stretch'},
      justifyContent: {base: 'center', lg: 'space-between'},
      rowGap: {base: '62px', lg: 0},
      columnGap: {
        base: `${PRESS_LOGOS_MOBILE_COLUMN_GAP_PX}px`,
        lg: `${PRESS_LOGOS_DESKTOP_COLUMN_GAP_PX}px`,
      },
      width: '100%',
      maxWidth: '1280px',
      margin: '0 auto',
      backgroundColor: 'transparent',
    },
    icon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: {base: `0 0 ${PRESS_LOGOS_MOBILE_COL_BASIS}`, lg: '0 0 auto'},
      width: {base: PRESS_LOGOS_MOBILE_COL_BASIS, lg: 'auto'},
      flexBasis: {base: PRESS_LOGOS_MOBILE_COL_BASIS, lg: 'auto'},
      minWidth: {base: 0, lg: 'auto'},
      maxWidth: {base: '143px', lg: 'none'},
      overflow: 'hidden',
      gap: 0,
      sx: {
        '.icon': {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          alignItems: 'center',
        },
        '.icon__link': {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        },
        '.icon__image': {
          width: {base: '100%', lg: 'auto'},
          maxWidth: '100%',
          height: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        '.icon__image svg': {
          width: {base: '100%', lg: 'auto'},
          height: 'auto',
          maxWidth: '100%',
          maxHeight: {base: '49px', lg: '50px'},
          display: 'block',
        },
        '.icon__image img': {
          width: 'auto',
          height: 'auto',
          maxWidth: '100%',
          maxHeight: {base: '49px', lg: '50px'},
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto',
        },
        '.icon__title, .icon__description': {
          display: 'none',
        },
      },
    },
  };
}

/**
 * IconSet – two layouts:
 * - pressLogos (CF-053): logo wall with optional eyebrow title.
 * - valueProp (CF-054): icon, title and description grid.
 * Both appearances use iconsCollection; backgroundColour and textColour apply to the wrapper.
 */
export const iconSetStyles = helpers.defineMultiStyleConfig({
  baseStyle({component}) {
    const bgHex = normalizeHex(component?.backgroundColour);
    const textHex = normalizeHex(component?.textColour);

    return {
      root: {
        backgroundColor: bgHex ?? 'white',
        overflow: 'hidden',
        ...(textHex && {color: textHex}),
      },
      wrapper: {},
      title: {
        display: 'none',
      },
      icon: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 0,
      },
    };
  },
  variants: {
    pressLogos: getPressLogosStyles,
    valueProp: getVariantStyles,
  },
  defaultProps: {
    variant: 'valueProp',
  },
});
